import {
  Backdrop,
  Box,
  CircularProgress,
  Container,
  makeStyles,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import React, { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from 'src/components/Header';
import Page from 'src/components/Page';
import SimpleAlert from 'src/components/SimpleAlert';
import {
  useGetStoreByIdQuery,
  useGetTimezonesQuery,
  useUpdateStoreMutation,
} from 'src/features/store/api/storeApi';
import type { StoreFormValues } from 'src/features/store/types/store.types';
import {
  fromStoreView,
  toStoreCrudPayload,
} from 'src/features/store/utils/storeFormPayload';
import { useApiErrorMessage } from 'src/hooks/useApiErrorMessage';
import useAppInsightPageView from 'src/hooks/useAppInsightPageView';
import useCommonStyles from 'src/hooks/useCommonStyles';
import { Theme } from 'src/theme';
import { getSelectedClientId } from 'src/utils/client';
import { truncate } from 'src/utils/common';
import { isExoApiError } from 'src/utils/errorHandlers';
import StoreForm from './StoreForm';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingTop: theme.spacing(3),
    paddingBottom: 100,
  },
}));

const breadcrumbs = [
  {
    name: 'Manage Stores',
    path: '/management/stores',
  },
];

const StoreEditPage: FC = () => {
  useAppInsightPageView({ name: 'ExoAdmin-Edit Store' });
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams<{ id: string }>();
  const storeId = Number(id);
  const clientId = getSelectedClientId();

  const {
    data: store,
    isLoading,
    error,
  } = useGetStoreByIdQuery(
    { id: storeId },
    { skip: !storeId, refetchOnMountOrArgChange: true },
  );
  const errorMessage = useApiErrorMessage(error);

  const { data: timezones = [] } = useGetTimezonesQuery();

  const [updateStore, { isLoading: isSubmitting }] = useUpdateStoreMutation();

  const handleSubmit = async (values: StoreFormValues): Promise<void> => {
    try {
      await updateStore({
        id: storeId,
        body: toStoreCrudPayload(values, clientId),
      }).unwrap();
      enqueueSnackbar(`${truncate(values.name)} has been updated.`, {
        variant: 'success',
        autoHideDuration: 3000,
      });
      navigate('/app/management/stores');
    } catch (err) {
      enqueueSnackbar(
        isExoApiError(err) ? err.data.detail : `Failed to update ${truncate(values.name)}.`,
        { variant: 'error', autoHideDuration: 3000 },
      );
    }
  };

  return (
    <Page className={classes.root} title="Edit Store" exoBgLogo={true}>
      <Backdrop className={commonClasses.backdrop} open={isLoading}>
        <CircularProgress color="primary" />
      </Backdrop>
      <Container maxWidth={false}>
        <Header
          breadcrumbs={breadcrumbs}
          pageTitle="Edit Store"
          showCancelButton={true}
          cancelLink="/app/management/stores"
          mainHeader="Management"
        />
        {error && errorMessage && (
          <Box mt={2}>
            <SimpleAlert severity="error" text={errorMessage} />
          </Box>
        )}
        {store && (
          <StoreForm
            mode="edit"
            initialValues={fromStoreView(store)}
            timezones={timezones}
            submitLabel="Update Store"
            isSubmitting={isSubmitting}
            hasLoyaltyProgramUsername={store.hasLoyaltyProgramUsername}
            hasLoyaltyProgramPassword={store.hasLoyaltyProgramPassword}
            onSubmit={handleSubmit}
          />
        )}
      </Container>
    </Page>
  );
};

export default StoreEditPage;
